/**
 * Project colours and the chart palette.
 *
 * One list, shared by the web app and (from Phase 5) the PDF renderer, so a project is the
 * same colour wherever it appears: the dot in the time views, its bar on the dashboard and
 * its swatch in a report.
 *
 * Each hue ships in two tones. `vivid` clears the categorical checks on its own; `pastel` is
 * deliberately light and does not — a pastel fill sits around 1.8:1 against a white card,
 * well under the 3:1 WCAG 2.1 AA asks of a meaningful graphic (1.4.11). Pastels are therefore
 * only ever drawn with `inkFor()` as an outline, which restores the contrast of the mark's
 * boundary, and colour is never the sole carrier of identity anywhere in the app (every dot,
 * bar and swatch sits next to its name).
 */
export const PROJECT_PALETTE = [
    { name: 'blue', vivid: '#2563EB', pastel: '#A5C8F2' },
    { name: 'cyan', vivid: '#0891B2', pastel: '#92D3E3' },
    { name: 'green', vivid: '#16A34A', pastel: '#A5DCB4' },
    { name: 'amber', vivid: '#F59E0B', pastel: '#F5D59A' },
    { name: 'red', vivid: '#DC2626', pastel: '#F2AFAF' },
    { name: 'pink', vivid: '#DB2777', pastel: '#F2B2D1' },
    { name: 'violet', vivid: '#7C3AED', pastel: '#C7BCF0' },
    { name: 'slate', vivid: '#6B7280', pastel: '#C9D0D9' },
];
/** Every selectable colour, vivid tones first (the order the picker renders). */
export const PROJECT_COLORS = [
    ...PROJECT_PALETTE.map((c) => c.vivid),
    ...PROJECT_PALETTE.map((c) => c.pastel),
];
/**
 * Fixed categorical order for series that carry no stored colour of their own (activities).
 * Assign by entity, never by rank, and never cycle past the end — `seriesColor` returns the
 * neutral tail colour instead, so a 9th series is not a second "blue".
 */
export const CHART_SERIES_COLORS = PROJECT_PALETTE.map((c) => c.vivid);
/** Neutral used for everything past the end of the categorical order. */
export const CHART_TAIL_COLOR = '#9AA1AC';
export function isHexColor(value) {
    return /^#[0-9A-Fa-f]{6}$/.test(value);
}
function toRgb(hex) {
    if (!isHexColor(hex))
        throw new Error(`Not a #RRGGBB colour: ${hex}`);
    const n = Number.parseInt(hex.slice(1), 16);
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
function toHex(rgb) {
    return `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}
/** WCAG relative luminance. */
export function relativeLuminance(hex) {
    const [r, g, b] = toRgb(hex).map((v) => {
        const c = v / 255;
        return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG contrast ratio between two colours, 1–21. */
export function contrastRatio(a, b) {
    const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
/** Minimum contrast WCAG 2.1 AA asks of a meaningful graphic against its background (1.4.11). */
export const GRAPHIC_CONTRAST_MIN = 3;
/**
 * The darker companion of a colour: the same hue, mixed toward black until it clears
 * {@link GRAPHIC_CONTRAST_MIN} against `background`. Used for the outline of a bar and the ring
 * of a dot, so even the lightest pastel has a boundary you can actually see.
 */
export function inkFor(hex, background = '#FFFFFF') {
    const rgb = toRgb(hex);
    // 5% steps are fine-grained enough to stay recognisably the same hue and always terminate:
    // black clears 3:1 against any background light enough to have failed in the first place.
    for (let mix = 0; mix <= 1.0001; mix += 0.05) {
        const candidate = toHex([rgb[0] * (1 - mix), rgb[1] * (1 - mix), rgb[2] * (1 - mix)]);
        if (contrastRatio(candidate, background) >= GRAPHIC_CONTRAST_MIN)
            return candidate;
    }
    return '#000000';
}
/**
 * Black or white, whichever is more readable on top of `hex` — for a tick or a label drawn
 * inside a swatch. `inkFor` is the wrong tool there: it stays in the same hue, which is
 * exactly what you cannot see against that hue.
 */
export function onColor(hex) {
    return contrastRatio(hex, '#000000') >= contrastRatio(hex, '#FFFFFF') ? '#000000' : '#FFFFFF';
}
/**
 * Colours for series that have none of their own (activities), keyed by id.
 *
 * Pass the keys in the order the chart shows them, largest first: the eight categorical slots
 * go to the eight largest series and everything past them takes the neutral tail colour. That
 * is the "fold the tail" rule — a ninth series is never a generated or recycled hue — and it
 * keeps the neutral on the small bars instead of landing arbitrarily on a big one.
 *
 * These colours are presentational. An activity carries no colour in the data model, and every
 * bar is named by its axis label, so nothing is identified by hue alone and nothing is lost
 * when the tail shares one.
 */
export function seriesColors(keys) {
    const ordered = [...new Set(keys)];
    return new Map(ordered.map((key, i) => [key, CHART_SERIES_COLORS[i] ?? CHART_TAIL_COLOR]));
}
//# sourceMappingURL=colors.js.map